/**
 * Phone — how each of this Company's numbers behaves when it rings.
 *
 * The number itself and whether it is live are not here: provisioning and
 * decommissioning a Twilio number are out-of-band operations, not
 * settings, so this screen only edits the rows that exist. There is no
 * add and no delete for the same reason.
 *
 * Most Companies have exactly one number, so the numbers are stacked
 * rather than listed and drilled into — a list of one is a step to get
 * past, not navigation.
 *
 * The screen is what the number does with every call: where it rings,
 * for how long, whether an answered call is recorded, and what a caller
 * hears — the greeting, which is played at answer to every caller before
 * the number rings through (twilio_webhook.go), and the voicemail prompt.
 *
 * The **Receptionist** is one step below it: Phone → Receptionist, opened
 * from the row at the foot of the screen, whose description says how it
 * is set at a glance — on or off, when it picks up, the name it gives.
 * Everything that is the Receptionist's alone is on that page: its
 * switch, when it answers, the name it gives, and what it may answer.
 * It was three screens once — Phone, then Receptionist config, then
 * Knowledge — and then a switch and its schedule on this screen with
 * what it may answer a page below, so a Company setting the thing up
 * read half of it here and half there. Which page is showing is a URL
 * place (`?page=`, committed with `replace`), so the page is linkable and
 * survives Back exactly as `?section=` does; with more than one number
 * it also names which (`&number=`), because a schedule and a name are a
 * number's while what it may answer is the Company's.
 *
 * What those numbers have actually taken is not here at all: it is the
 * Inquiries destination, one bar above (#85). The settings say what will
 * happen to a call; what did happen is not a setting, and a Company reads
 * it far more often than it edits any of this. What a caller is *asked*
 * is not here either — that is one list for the form and the phone
 * alike, and it is Settings → Questions (ADR-0022).
 */
import { useRef } from "react";
import { useSearchParams } from "react-router";
import { Banner } from "../../components/ui/Banner";
import {
  NumberSettingRow,
  SettingRow,
  TextSettingRow,
  ToggleSettingRow,
} from "../../components/ui/SettingRow";
import { useSettingCommit } from "../../lib/use-setting-commit";
import { blurOnEnter } from "../../lib/use-action";
import { useSettingsSource } from "../../lib/use-settings-source";
import {
  listPhoneNumbers,
  savePhoneNumber,
  type CompanySettings,
  type CompanySettingsUpdate,
  type PhoneNumber,
  type PhoneNumberUpdate,
} from "../../lib/settings-api";
import { PromptRecording } from "./PromptRecording";
import { ReceptionistKnowledge } from "./ReceptionistKnowledge";
import {
  BackLink,
  DrillDownRow,
  DrillList,
  Loading,
  SectionHeader,
  SettingsGroup,
  TextAreaField,
} from "./section-chrome";
import styles from "./PhoneSection.module.css";

/** The one page Phone opens, as it reads in `?page=`. */
const RECEPTIONIST_PAGE = "receptionist";
/**
 * What the page was called while it held only what the Receptionist may
 * answer; a link already sent to it still lands.
 */
const OLD_KNOWLEDGE_PAGE = "receptionist-knowledge";

type PatchNumber = (
  id: string,
  fields: Omit<PhoneNumberUpdate, "id">,
) => Promise<void>;

export function PhoneSection({
  settings,
  onPatch,
}: {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: numbers,
    setData: setNumbers,
    loadError,
  } = useSettingsSource(listPhoneNumbers);

  /** `?page=` is set and cleared in place: a drill is not history. */
  const showPage = (page: string | null, numberId?: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete("number");
    if (page === null) next.delete("page");
    else next.set("page", page);
    if (page !== null && numberId) next.set("number", numberId);
    setSearchParams(next, { replace: true });
  };

  /** What the server answered with replaces that number. */
  const replace = (saved: PhoneNumber) =>
    setNumbers((current) =>
      (current ?? []).map((n) => (n.id === saved.id ? saved : n)),
    );

  /** One PATCH per committed row; the response replaces that number. */
  const patch: PatchNumber = async (id, fields) => {
    replace(await savePhoneNumber({ id, ...fields }));
  };

  const page = searchParams.get("page");
  if (page === RECEPTIONIST_PAGE || page === OLD_KNOWLEDGE_PAGE) {
    // The number named in the URL, or the first where none is named or
    // the one named is not this Company's — most Companies have one.
    const requested = searchParams.get("number");
    const number = numbers
      ? (numbers.find((n) => n.id === requested) ?? numbers[0] ?? null)
      : null;
    return (
      <div>
        <BackLink label="Phone" onClick={() => showPage(null)} />
        <ReceptionistPage
          numbers={numbers}
          number={number}
          loadError={loadError}
          settings={settings}
          onPatch={onPatch}
          onPatchNumber={patch}
        />
      </div>
    );
  }

  return (
    <section>
      <SectionHeader
        title="Phone"
        description="What happens when someone rings your number."
      />

      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}

      <div className={styles.groups}>
        {!numbers ? (
          <Loading />
        ) : numbers.length === 0 ? (
          <p className={styles.empty}>
            No number yet. We set one up for you.
          </p>
        ) : (
          <>
            {numbers.map((number) => (
              <NumberGroup
                key={number.id}
                number={number}
                onPatch={patch}
                onChanged={replace}
              />
            ))}

            {/* No group heading: one row does not need a name and a
                description above it saying the same thing twice. The
                rows that open pages are the last thing on the screen, so
                two numbers are two rows here, each naming its number,
                rather than a row inside each number's group. */}
            <DrillList>
              {numbers.map((number) => (
                <DrillDownRow
                  key={number.id}
                  name={
                    numbers.length > 1
                      ? `Receptionist config for ${number.e164}`
                      : "Receptionist config"
                  }
                  onOpen={() =>
                    showPage(
                      RECEPTIONIST_PAGE,
                      numbers.length > 1 ? number.id : undefined,
                    )
                  }
                />
              ))}
            </DrillList>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Phone → Receptionist: everything that is the Receptionist's alone, for
 * one number, with what it may answer — the Company's, not the number's —
 * under it.
 *
 * "When it answers" is a dependent group: the schedule means nothing
 * while the Receptionist is off, so it is drawn only while it is on
 * (DESIGN.md, dependent settings). The name and what it may answer stay
 * either way — they are what it will say once it is switched on, and
 * hiding them would leave a reader looking for what this page is named
 * after.
 */
function ReceptionistPage({
  numbers,
  number,
  loadError,
  settings,
  onPatch,
  onPatchNumber,
}: {
  numbers: PhoneNumber[] | null;
  number: PhoneNumber | null;
  loadError: string | null;
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
  onPatchNumber: PatchNumber;
}) {
  const several = (numbers?.length ?? 0) > 1;

  return (
    <section>
      <SectionHeader
        title="Receptionist"
        description={
          several && number
            ? `Answers the calls nobody picks up on ${number.e164}.`
            : "Answers the calls nobody picks up."
        }
      />

      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}

      <div className={styles.groups}>
        {!numbers ? (
          <Loading />
        ) : (
          number && <NumberReceptionist number={number} onPatch={onPatchNumber} />
        )}

        <ReceptionistKnowledge settings={settings} onPatch={onPatch} />
      </div>
    </section>
  );
}

/** One number's switch, its schedule, and what it says. */
function NumberReceptionist({
  number,
  onPatch,
}: {
  number: PhoneNumber;
  onPatch: PatchNumber;
}) {
  const save = (fields: Omit<PhoneNumberUpdate, "id">) =>
    onPatch(number.id, fields);

  return (
    <>
      {/* The page's own switch, with no heading over it: the page is
          named for it already. */}
      <div className={styles.lone}>
        <ToggleSettingRow
          id={`phone-receptionist-${number.id}`}
          title="Receptionist"
          checked={number.receptionist_enabled}
          onChange={(receptionist_enabled) => save({ receptionist_enabled })}
        />
      </div>

      {number.receptionist_enabled && (
        <SettingsGroup
          id={`receptionist-when-${number.id}`}
          title="When it answers"
        >
          <SetTimesRow number={number} onSave={save} />
          {/* Dependent settings: rendered only while set times is on. */}
          {number.receptionist_set_times && (
            <>
              <PicksUpFromRow number={number} onSave={save} />
              <PicksUpOnRow number={number} onSave={save} />
            </>
          )}
        </SettingsGroup>
      )}

      <SettingsGroup id={`receptionist-spoken-${number.id}`} title="What it says">
        <TextSettingRow
          id={`phone-receptionist-name-${number.id}`}
          title="Name"
          description="The name it gives callers."
          optional
          value={number.receptionist_name}
          onSave={(receptionist_name) => save({ receptionist_name })}
        />
      </SettingsGroup>
    </>
  );
}

function NumberGroup({
  number,
  onPatch,
  onChanged,
}: {
  number: PhoneNumber;
  onPatch: PatchNumber;
  /** A recording set or removed: the number as the server now holds it. */
  onChanged: (next: PhoneNumber) => void;
}) {
  const save = (fields: Omit<PhoneNumberUpdate, "id">) =>
    onPatch(number.id, fields);

  return (
    <SettingsGroup id={`phone-${number.id}`} title={number.e164}>
      <TextSettingRow
        id={`phone-forward-${number.id}`}
        title="Rings through to"
        description="The number your people answer on."
        value={number.forward_to}
        required
        onSave={(forward_to) => save({ forward_to })}
      />
      <RingTimeRow number={number} onSave={save} />

      <ToggleSettingRow
        id={`phone-recording-${number.id}`}
        title="Record answered calls"
        checked={number.recording_enabled}
        onChange={(recording_enabled) => save({ recording_enabled })}
      />

      {/* The greeting is played at answer to every caller, before the
          number rings through (twilio_webhook.go) — not only to the ones
          the Receptionist takes — so it is the number's, and here,
          rather than on the Receptionist's page. Each prompt may be the
          Company's own recording (migration 076), and a recording takes
          the box's place: it is what callers hear, so it is what the
          field shows. The words stay stored and are said whenever it
          cannot be played; removing the recording brings the box back. */}
      <TextAreaField
        id={`phone-greeting-${number.id}`}
        label="Greeting"
        description="Played to every caller before your phone rings."
        tooltip="Type the words for a computer voice to say, or press the microphone to record your own voice instead. Your recording must tell callers the call is recorded."
        replaced={!!number.greeting_recording_url}
        value={number.greeting}
        rows={3}
        onSave={(greeting) => save({ greeting })}
      >
        <PromptRecording
          number={number}
          prompt="greeting"
          onChanged={onChanged}
        />
      </TextAreaField>

      <TextAreaField
        id={`phone-voicemail-${number.id}`}
        label="Voicemail prompt"
        description="Played when nobody answers and the Receptionist is off."
        tooltip="Type the words for a computer voice to say, or press the microphone to record your own voice instead."
        replaced={!!number.voicemail_recording_url}
        value={number.voicemail_prompt}
        rows={3}
        onSave={(voicemail_prompt) => save({ voicemail_prompt })}
      >
        <PromptRecording
          number={number}
          prompt="voicemail"
          onChanged={onChanged}
        />
      </TextAreaField>
    </SettingsGroup>
  );
}

/** The ring times the API accepts, the same bounds as migration 038. */
const MIN_RING_SECS = 5;
const MAX_RING_SECS = 60;

/**
 * How long the number rings before a missed call goes to the Receptionist
 * or voicemail (#194). Commits on blur like any text row; a value outside
 * the bounds is held with a message rather than sent to be refused, and an
 * emptied field goes back to what is stored.
 *
 * The description says what a long ring costs: nothing waits for a
 * keypress before the call is put through, so a phone that goes to its own
 * voicemail sooner hands the caller to that voicemail.
 */
function RingTimeRow({
  number,
  onSave,
}: {
  number: PhoneNumber;
  onSave: (fields: Omit<PhoneNumberUpdate, "id">) => Promise<void>;
}) {
  return (
    <NumberSettingRow
      id={`phone-ring-${number.id}`}
      title="Rings for"
      description="How long your phone rings before the Receptionist or voicemail picks up."
      value={number.ring_secs}
      onSave={(ring_secs) => onSave({ ring_secs })}
      min={MIN_RING_SECS}
      max={MAX_RING_SECS}
      integer
      unit="seconds"
    />
  );
}

/**
 * Whether the Receptionist picks up at any hour or only at the times set
 * under it (migration 046). Off, the default, is every call nobody
 * answers. The switch is the only thing it saves: the hours and days
 * under it are kept while it is off, so turning it back on finds them as
 * they were.
 *
 * It asked for the office's hours between migrations 045 and 046, under a
 * switch that said it picks up when nobody does, and a reader had to turn
 * one into the other to work out when it answers. Every row now says when
 * the Receptionist picks up.
 *
 * The copy is the same on and off: a row that rewrites itself as it is
 * switched reads as a different setting each time it is looked at, so it
 * says what on does and what off does, once.
 */
function SetTimesRow({
  number,
  onSave,
}: {
  number: PhoneNumber;
  onSave: (fields: Omit<PhoneNumberUpdate, "id">) => Promise<void>;
}) {
  return (
    <ToggleSettingRow
      id={`phone-set-times-${number.id}`}
      title="Only at set times"
      description={`Picks up only at the hours and days you set. If left off, it picks up any call nobody answers in ${number.ring_secs} seconds.`}
      checked={number.receptionist_set_times}
      onChange={(receptionist_set_times) => onSave({ receptionist_set_times })}
    />
  );
}

/** The Receptionist's hours as one value, so the pair commits together. */
interface PicksUpHours {
  from: string;
  until: string;
}

const sameHours = (a: PicksUpHours, b: PicksUpHours) =>
  a.from === b.from && a.until === b.until;

/**
 * The hours the Receptionist picks up on the days below, or all day on
 * them with both left blank. A window that ends before it starts runs
 * overnight, into the next morning.
 *
 * Both times on one row: the hours are one setting in two fields, and the
 * API refuses one without the other. Each input commits on blur (Enter
 * blurs), but leaving one input for the other is not leaving the row, so
 * nothing is sent until focus leaves the pair.
 */
function PicksUpFromRow({
  number,
  onSave,
}: {
  number: PhoneNumber;
  onSave: (fields: Omit<PhoneNumberUpdate, "id">) => Promise<void>;
}) {
  const fromRef = useRef<HTMLInputElement>(null);
  const untilRef = useRef<HTMLInputElement>(null);
  const { draft, setDraft, onFocus, saving, error, setError, commitDraft } =
    useSettingCommit<PicksUpHours>(
      { from: number.receptionist_from, until: number.receptionist_until },
      (next) =>
        onSave({ receptionist_from: next.from, receptionist_until: next.until }),
      { equals: sameHours },
    );

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const to = e.relatedTarget;
    if (to && (to === fromRef.current || to === untilRef.current)) return;
    if ((draft.from === "") !== (draft.until === "")) {
      setError("Enter both times, or leave both blank.");
      return;
    }
    if (draft.from !== "" && draft.from === draft.until) {
      setError("Start and finish must be different times.");
      return;
    }
    void commitDraft();
  };
  const fromId = `phone-picks-up-from-${number.id}`;

  return (
    <SettingRow
      title="Picks up from"
      description="Times it picks up. If left blank, it picks up for all hours on selected days."
      htmlFor={fromId}
      error={error}
      indent
    >
      <div className={styles.inlineField}>
        <input
          ref={fromRef}
          id={fromId}
          type="time"
          className={styles.timeInput}
          value={draft.from}
          disabled={saving}
          onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={blurOnEnter}
        />
        <span className={styles.unit}>to</span>
        <input
          ref={untilRef}
          id={`phone-picks-up-until-${number.id}`}
          type="time"
          aria-label="Picks up until"
          className={styles.timeInput}
          value={draft.until}
          disabled={saving}
          onChange={(e) => setDraft({ ...draft, until: e.target.value })}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={blurOnEnter}
        />
      </div>
    </SettingRow>
  );
}

/** ISO weekdays, Monday 1, as the API stores them. */
const WEEKDAYS = [
  { day: 1, short: "M", name: "Monday" },
  { day: 2, short: "T", name: "Tuesday" },
  { day: 3, short: "W", name: "Wednesday" },
  { day: 4, short: "T", name: "Thursday" },
  { day: 5, short: "F", name: "Friday" },
  { day: 6, short: "S", name: "Saturday" },
  { day: 7, short: "S", name: "Sunday" },
];

const sameDays = (a: number[], b: number[]) =>
  a.length === b.length && a.every((d, i) => d === b[i]);

/**
 * The days the Receptionist picks up on, as a row of pressed and
 * unpressed days: a pressed day is one it is on. A day commits when it is
 * pressed, as a toggle does; there is nothing to blur.
 *
 * The last pressed day cannot be let go. No days would be a Receptionist
 * that is on and never picks up, a second way to turn it off beside the
 * switch that already does, so the row says one day is required and the
 * API refuses an empty list.
 *
 * Raw buttons rather than `Button`: seven one-letter days are a toggle
 * group, and `Button`'s padding and weight are a single action's.
 */
function PicksUpOnRow({
  number,
  onSave,
}: {
  number: PhoneNumber;
  onSave: (fields: Omit<PhoneNumberUpdate, "id">) => Promise<void>;
}) {
  const { saving, error, commit } = useSettingCommit<number[]>(
    number.receptionist_days,
    (next) => onSave({ receptionist_days: next }),
    { equals: sameDays },
  );
  const on = new Set(number.receptionist_days);
  const labelId = `phone-picks-up-on-${number.id}`;

  const flip = (day: number) => {
    const next = new Set(on);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    if (next.size === 0) return;
    void commit([...next].sort((a, b) => a - b));
  };

  return (
    <SettingRow
      title="Picks up on"
      description="Days it picks up. You must select at least one day."
      labelId={labelId}
      error={error}
      indent
    >
      <div role="group" aria-labelledby={labelId} className={styles.days}>
        {WEEKDAYS.map(({ day, short, name }) => {
          const pressed = on.has(day);
          const last = pressed && on.size === 1;
          return (
            <button
              key={day}
              type="button"
              aria-label={name}
              aria-pressed={pressed}
              aria-disabled={last || undefined}
              disabled={saving}
              onClick={() => flip(day)}
              className={`${styles.day} ${last ? styles.dayLast : ""} ${
                pressed ? styles.dayOn : styles.dayOff
              }`}
            >
              {short}
            </button>
          );
        })}
      </div>
    </SettingRow>
  );
}
